/**
 * Hook do status detalhado por dataset Zeus (Task #87).
 *
 * Mesmo padrao do useLastSync (#63): cache module-level + refetch
 * periodico (5min). Diferenca: cobre os 3 datasets (trades, curva
 * forward, dimensions) em vez de so o source primario.
 *
 * Em caso de falha (rede/auth/5xx) devolve null no `data` + flag de
 * erro — UI consumidora (ZeusSyncBadge) renderiza "Zeus: ?" cinza
 * sem quebrar TopBar.
 */
import { useEffect, useState } from "react";
import { getZeusIntegracaoStatus } from "../api/client";
import type { ZeusIntegracaoStatusResponse } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 5 * 60 * 1000; // 5 minutos

interface CacheEntry {
  expiresAt: number;
  promise: Promise<ZeusIntegracaoStatusResponse | null>;
}

let cache: CacheEntry | null = null;

function fetchStatus(): Promise<ZeusIntegracaoStatusResponse | null> {
  return getZeusIntegracaoStatus().catch(() => null);
}

export function invalidateZeusStatusCache(): void {
  cache = null;
}

export interface UseZeusStatusState {
  data: ZeusIntegracaoStatusResponse | null;
  isLoading: boolean;
  error: Error | null;
}

export function useZeusStatus(): UseZeusStatusState {
  const [state, setState] = useState<UseZeusStatusState>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;

    function load() {
      const now = Date.now();
      if (!cache || cache.expiresAt < now) {
        cache = { expiresAt: now + REFETCH_MS, promise: fetchStatus() };
      }
      cache.promise
        .then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          setState({ data: null, isLoading: false, error: err });
        });
    }

    load();
    // #75: tick skipa com a aba em background; volta visivel/focus -> 1 load.
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, []);

  return state;
}
